import { CharityVettingUrlType } from 'features/charity-vetting/types/charityVettingTypes';
import { z } from 'zod';

import ptMsg from './messages';

import type {
  PageSchemaFactory,
  PageSchemaMap,
} from 'features/charity-vetting/common-flow/validation/pageSchemaRegistry';

/**
 * Per-page Zod schemas for the PT vetting flow. NIF/NIPC are 9-digit fiscal
 * numbers (stored as number|null but validated as their string form).
 */

const wrap = (charityOnboarding: z.ZodTypeAny) => z.looseObject({ charityOnboarding });

const NINE_DIGITS = /^\d{9}$/;

const toStr = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

const ptRegistrationPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ charityType: z.string().min(1) }));

const nifPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z.looseObject({ nif: z.unknown().optional() }).superRefine((data, ctx) => {
      const value = toStr(data.nif);
      if (!value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(ptMsg.charityVettingRequiredNIFPT),
          path: ['nif'],
        });
        return;
      }
      if (!NINE_DIGITS.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(ptMsg.charityVettingWrongNifFormatNumberPT, {
            format: '123456789',
          }),
          path: ['nif'],
        });
      }
    }),
  );

const nipcPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z.looseObject({ nipc: z.unknown().optional() }).superRefine((data, ctx) => {
      const value = toStr(data.nipc);
      if (!value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(ptMsg.charityVettingRequiredNIPCPT),
          path: ['nipc'],
        });
        return;
      }
      if (!NINE_DIGITS.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage(ptMsg.charityVettingWrongNipcFormatNumberPT),
          path: ['nipc'],
        });
      }
    }),
  );

const fiscalBenefitsPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ fiscalBenefitsArticle: z.boolean() }));

const preparationKitchenPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ preparationKitchen: z.boolean() }));

const haccpPageSchema: PageSchemaFactory = () => wrap(z.looseObject({ haccp: z.boolean() }));

export const ptPageSchemas: PageSchemaMap = {
  [CharityVettingUrlType.REGISTRATION_DETAILS]: ptRegistrationPageSchema,
  [CharityVettingUrlType.NIF]: nifPageSchema,
  [CharityVettingUrlType.NIPC]: nipcPageSchema,
  [CharityVettingUrlType.FISCAL_BENEFITS]: fiscalBenefitsPageSchema,
  [CharityVettingUrlType.DONATION_HAS_KITCHEN]: preparationKitchenPageSchema,
  [CharityVettingUrlType.DONATION_HACCP]: haccpPageSchema,
};
